import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { API_BASE_URL } from '../config';

// 直接导入翻译文件
import zhHansCommon from './locales/zh-hans/common.json';
import enCommon from './locales/en/common.json';
import frCommon from './locales/fr/common.json';

// 支持的语言列表
export const SUPPORTED_LANGUAGES = {
  'zh': {
    name: '简体中文',
    nativeName: '简体中文',
    flag: '🇨🇳',
    direction: 'ltr'
  },
  'en': {
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸',
    direction: 'ltr'
  },
  'fr': {
    name: 'Français',
    nativeName: 'Français',
    flag: '🇫🇷',
    direction: 'ltr'
  }
};

// 默认语言
const DEFAULT_LANGUAGE = 'zh';

// 检测是否在后端管理界面
const isBackendInterface = () => {
  return window.location.pathname.startsWith('/dashboard');
};

// 获取用户首选语言
const getUserLanguage = () => {
  // 如果是后端管理界面，强制使用中文
  if (isBackendInterface()) {
    return 'zh';
  }
  
  // 从localStorage获取用户选择的语言
  const savedLanguage = localStorage.getItem('i18nextLng');
  if (savedLanguage && SUPPORTED_LANGUAGES[savedLanguage]) {
    return savedLanguage;
  }
  
  // 检测浏览器语言
  const browserLanguage = navigator.language || navigator.userLanguage;
  const shortLang = browserLanguage.split('-')[0];
  
  if (SUPPORTED_LANGUAGES[shortLang]) {
    return shortLang;
  }
  
  return DEFAULT_LANGUAGE;
};

// i18n配置
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // 初始语言
    lng: getUserLanguage(),
    
    // 回退语言
    fallbackLng: DEFAULT_LANGUAGE,
    
    // 调试模式
    debug: process.env.NODE_ENV === 'development',
    
    // 插值配置
    interpolation: {
      escapeValue: false, // React已经处理了XSS
    },
    
    // 检测器配置
    detection: {
      order: ['localStorage'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    
    // 本地资源
    resources: {
      'zh': {
        common: zhHansCommon
      },
      'en': {
        common: enCommon
      },
      'fr': {
        common: frCommon
      }
    },
    
    // 命名空间
    ns: ['common'],
    defaultNS: 'common',
    
    // 支持的语言
    supportedLngs: Object.keys(SUPPORTED_LANGUAGES),
    
    // 语言切换时的回调
    react: {
      useSuspense: false,
    },
  });

// 语言切换函数
export const changeLanguage = async (languageCode) => {
  // 如果是后端管理界面，不允许切换语言
  if (isBackendInterface()) {
    console.warn('Language switching is not allowed in backend interface');
    return false;
  }
  
  if (!SUPPORTED_LANGUAGES[languageCode]) {
    console.warn(`Unsupported language: ${languageCode}`);
    return false;
  }
  
  try {
    await i18n.changeLanguage(languageCode);
    localStorage.setItem('i18nextLng', languageCode);
    
    // 通知后端语言变更
    fetch(`${API_BASE_URL}/api/i18n/set-language/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ language_code: languageCode }),
      credentials: 'include',
    }).catch(error => {
      console.warn('Failed to notify backend of language change:', error);
    });
    
    return true;
  } catch (error) {
    console.error('Failed to change language:', error);
    return false;
  }
};

// 获取当前语言信息
export const getCurrentLanguage = () => {
  const currentLang = i18n.language;
  return SUPPORTED_LANGUAGES[currentLang] || SUPPORTED_LANGUAGES[DEFAULT_LANGUAGE];
};

// 获取支持的语言列表
export const getSupportedLanguages = () => {
  return SUPPORTED_LANGUAGES;
};

// 检查语言是否支持
export const isLanguageSupported = (languageCode) => {
  return !!SUPPORTED_LANGUAGES[languageCode];
};

export default i18n; 